import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Cookie, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, not changeable
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Show banner after 1 second
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie-consent", JSON.stringify(consent));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie-consent", JSON.stringify(consent));
    setShowBanner(false);
  };

  const savePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem("cookie-consent", JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <Card className="max-w-4xl mx-auto p-6 shadow-2xl border-2">
            <div className="flex items-start gap-4">
              <Cookie className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">
                  🍪 Wir verwenden Cookies
                </h3>
                
                {!showSettings ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      Wir verwenden Cookies und ähnliche Technologien, um Ihnen ein optimales Nutzungserlebnis zu bieten. 
                      Einige Cookies sind für den Betrieb der Website notwendig, während andere uns helfen, die Website und Ihr 
                      Nutzungserlebnis zu verbessern. Sie können selbst entscheiden, welche Kategorien Sie zulassen möchten.
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={acceptAll} size="sm">
                        Alle akzeptieren
                      </Button>
                      <Button onClick={acceptNecessary} variant="outline" size="sm">
                        Nur notwendige
                      </Button>
                      <Button 
                        onClick={() => setShowSettings(true)} 
                        variant="ghost" 
                        size="sm"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Einstellungen
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground mt-3">
                      Mehr Informationen finden Sie in unserer{" "}
                      <Link to="/datenschutz" className="underline hover:text-primary">
                        Datenschutzerklärung
                      </Link>
                      {" "}und im{" "}
                      <Link to="/impressum" className="underline hover:text-primary">
                        Impressum
                      </Link>.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="space-y-4 mb-4">
                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">Notwendige Cookies</p>
                          <p className="text-xs text-muted-foreground">
                            Erforderlich für grundlegende Funktionen der Website
                          </p>
                        </div>
                        <div className="text-sm font-medium text-green-600">
                          Immer aktiv
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">Analyse-Cookies</p>
                          <p className="text-xs text-muted-foreground">
                            Helfen uns, die Nutzung der Website zu verstehen
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.analytics}
                            onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">Marketing-Cookies</p>
                          <p className="text-xs text-muted-foreground">
                            Werden verwendet, um Ihnen relevante Werbung anzuzeigen
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.marketing}
                            onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={savePreferences} size="sm">
                        Auswahl speichern
                      </Button>
                      <Button 
                        onClick={() => setShowSettings(false)} 
                        variant="outline" 
                        size="sm"
                      >
                        Zurück
                      </Button>
                    </div>
                  </>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="flex-shrink-0"
                onClick={acceptNecessary}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
